/**
 * The main character — a neutral adult who lives at the left of the street and,
 * when the user clicks an upload zone, walks over to it. Commanded imperatively
 * from the world:
 *   command('scan', x) → walk to x, then mime a phone scan (glowing screen +
 *                        sweeping beam) — house / car.
 *   command('die',  x) → walk to x, get struck (flash) + collapse; a long arm
 *                        with a phone juts up to scan anyway — Reaper/life gag.
 *   command('fly',  x) → walk to x (suitcases), board a plane that flies off
 *                        tail-first, then reappear for the standing scan.
 *   command('wave', x) → wave hello at the viewer (logged-in person click).
 *   command('idle')    → walk back home, then stand idle (also the login spot).
 *
 * Drawn into a single Graphics, redrawn each frame (cheap — one small figure).
 * Facing is a horizontal mirror around the figure's centre (pivot.x = 13).
 */
import { Container, Graphics } from 'pixi.js'
import { PALETTE as P } from './palette'
import { rect, poly } from './draw'
import { makeOutline, drawDash8 } from './objects'
import { CHARACTER_HOME_X, ZONE_RECTS } from '../sceneLayout'

export interface Character {
  container: Container
  command(mode: 'idle' | 'scan' | 'die' | 'fly' | 'wave', x: number): void
  /**
   * Number of insured zones (0..5). Drives the "level up" accessory ladder on
   * the upright figure: 1 → sunglasses, 2 → top hat, 3 → gold chain (replaces
   * the red scarf), 4 → briefcase, 5 → gold sparkles twinkling around.
   */
  setLevel(level: number): void
  update(dtMs: number, timeMs: number): void
}

type Pose = 'idle' | 'walk' | 'scan' | 'falling' | 'fallen' | 'flying' | 'waving'
type Arrive = 'idle' | 'scan' | 'fall' | 'fly' | 'wave'

const BASE_TOP = ZONE_RECTS.person.y // figure top; feet land on the pavement
const FALL_MS = 420 // collapse beat before the body settles on the ground
const BOARD_MS = 380 // plane slides in beside the character
const FLY_MS = 900 // plane accelerates off-screen tail-first

/** Size of the fly-away gag plane (uses the shared big Q400 model). */
const FLY_PLANE_S = 1.3

function drawBody(
  g: Graphics,
  pose: Pose,
  frame: number,
  timeMs: number,
  stageElapsed: number,
  level: number,
): void {
  g.clear()

  // ─── Fallen: corpse lying flat + a long arm with a phone, still scanning. ──
  if (pose === 'fallen') {
    rect(g, 0, 46, 4, 4, P.shoes)
    rect(g, 3, 46, 9, 5, P.trousers)
    rect(g, 11, 44, 9, 7, P.coat)
    rect(g, 11, 49, 9, 2, P.coatDark)
    rect(g, 19, 44, 6, 6, P.skin)
    rect(g, 19, 43, 6, 2, P.hair)
    rect(g, 21, 46, 1, 1, P.shadow, 0.7)
    rect(g, 23, 46, 1, 1, P.shadow, 0.7)
    rect(g, 15, 0, 3, 45, P.coat)
    rect(g, 17, 0, 1, 45, P.coatDark)
    rect(g, 14, -3, 4, 4, P.skin)
    rect(g, 12, -11, 7, 9, P.phone)
    rect(g, 13, -10, 5, 7, P.phoneScreen)
    const pulse = 0.55 + 0.45 * Math.sin(timeMs / 120)
    rect(g, 13, -10, 5, 2, P.phoneScreenBright, pulse)
    const sweep = (timeMs / 520) % 1
    poly(g, [19, -9, 30, -14 + sweep * 6, 30, -6 + sweep * 6], P.scanBeam, 0.2)
    rect(g, 19, Math.round(-10 + sweep * 6), 11, 1, P.scanBeam, 0.5)
    return
  }

  // ─── Falling: struck pose + white impact flash. ────────────────────────────
  if (pose === 'falling') {
    rect(g, 7, 43, 4, 8, P.trousers)
    rect(g, 15, 43, 4, 8, P.trousers)
    rect(g, 6, 50, 6, 2, P.shoes)
    rect(g, 15, 50, 6, 2, P.shoes)
    rect(g, 6, 25, 15, 19, P.coat)
    rect(g, 16, 25, 5, 19, P.coatDark)
    rect(g, 10, 17, 8, 8, P.skin)
    rect(g, 10, 15, 9, 3, P.hair)
    rect(g, 2, 19, 4, 9, P.coatDark)
    rect(g, 20, 19, 4, 9, P.coatDark)
    rect(g, 2, 16, 4, 3, P.skin)
    rect(g, 20, 16, 4, 3, P.skin)
    const a = Math.max(0, 1 - stageElapsed / 260) * 0.7
    if (a > 0.01) g.ellipse(13, 30, 20, 26).fill({ color: 0xffffff, alpha: a })
    return
  }

  // ─── Flying: once boarded, only the (big Q400) plane carrying the character
  //     + suitcases is drawn, accelerating off-screen tail-first. ──────────────
  const planeOpts = { noseRight: true, gear: false } as const
  if (pose === 'flying' && stageElapsed >= BOARD_MS) {
    const flyT = Math.min(1, (stageElapsed - BOARD_MS) / FLY_MS)
    drawDash8(g, 3 - flyT * flyT * 500, -6 - flyT * 52, FLY_PLANE_S, planeOpts)
    return
  }
  // Boarding sub-stage: the plane drops in over the suitcases — fall through to
  // draw the upright figure (boarding) too.
  if (pose === 'flying') {
    const board = stageElapsed / BOARD_MS
    drawDash8(g, 110 - board * 107, -6, FLY_PLANE_S, planeOpts)
  }

  // ─── Upright figure (idle / walk / scan / boarding) ────────────────────────
  if (pose === 'walk') {
    if (frame === 0) {
      rect(g, 6, 36, 4, 12, P.trousers)
      rect(g, 5, 47, 6, 4, P.shoes)
      rect(g, 15, 36, 4, 11, P.trousers)
      rect(g, 16, 46, 5, 4, P.shoes)
    } else {
      rect(g, 8, 36, 4, 11, P.trousers)
      rect(g, 7, 46, 5, 4, P.shoes)
      rect(g, 14, 36, 4, 12, P.trousers)
      rect(g, 15, 47, 6, 4, P.shoes)
    }
  } else {
    rect(g, 8, 36, 4, 13, P.trousers)
    rect(g, 14, 36, 4, 13, P.trousers)
    rect(g, 8, 36, 1, 13, 0x47464d)
    rect(g, 7, 48, 5, 4, P.shoes)
    rect(g, 14, 48, 5, 4, P.shoes)
  }

  rect(g, 5, 15, 16, 22, P.coat)
  rect(g, 16, 15, 5, 22, P.coatDark)
  rect(g, 12, 16, 1, 20, P.coatDark, 0.7)
  rect(g, 9, 15, 8, 3, P.coatDark)
  rect(g, 11, 12, 4, 3, P.skinShadow)
  rect(g, 9, 3, 8, 9, P.skin)
  rect(g, 16, 4, 1, 8, P.skinShadow)
  rect(g, 8, 1, 10, 4, P.hair)
  rect(g, 8, 3, 2, 5, P.hair)
  rect(g, 16, 3, 2, 4, P.hair)
  // ─── neckwear: red scarf (default) or a gold chain once 3+ zones are insured ─
  if (level >= 3) {
    rect(g, 7, 13, 11, 3, 0xd9b45a) // gold collar
    rect(g, 7, 13, 11, 1, 0xf4d77a) // top highlight
    rect(g, 7, 15, 11, 1, 0xa98b35) // shadow line
    rect(g, 11, 16, 5, 4, 0xd9b45a) // dangling medallion
    rect(g, 12, 17, 3, 2, 0xa98b35) // medallion centre
  } else {
    rect(g, 7, 13, 11, 3, 0xc4443a) // scarf around the neck
    rect(g, 13, 15, 4, 7, 0xc4443a) // hanging end
    rect(g, 13, 15, 4, 1, 0x8f2f28)
  }
  const blink = timeMs % 3400 < 150 // a blink every ~3.4s
  const ex = Math.round(Math.sin(timeMs / 2200) * 1.2) // slow left/right glance
  rect(g, 11, 6, 1, 1, P.hair) // eyebrows
  rect(g, 14, 6, 1, 1, P.hair)
  if (blink) {
    rect(g, 10 + ex, 8, 5, 1, P.skinShadow) // eyes closed
  } else {
    rect(g, 11 + ex, 7, 1, 2, 0x2b2620) // eyes
    rect(g, 14 + ex, 7, 1, 2, 0x2b2620)
  }
  // ─── Accessory: "deal-with-it" boss shades (level 1+) ────────────────────
  // Wide angular black lenses with the iconic diagonal white reflections.
  // Static — no glance offset — so they sit firmly on the face.
  if (level >= 1) {
    const black = 0x000000
    const white = 0xffffff
    // Top frame bar (spans both lenses + bridge)
    rect(g, 6, 5, 13, 1, black)
    // Lenses — 5 wide × 3 tall, plus a stepped row dropping the outer edge
    // a pixel lower for that wraparound look.
    rect(g, 6, 6, 5, 3, black) // left lens body
    rect(g, 7, 9, 3, 1, black) // left lens bottom step
    rect(g, 14, 6, 5, 3, black) // right lens body
    rect(g, 15, 9, 3, 1, black) // right lens bottom step
    // Nose bridge (top + a 1-pixel link through the lens area)
    rect(g, 11, 5, 3, 1, black)
    rect(g, 12, 6, 2, 1, black)
    // Temple stubs poking past the face on either side
    rect(g, 5, 7, 1, 1, black)
    rect(g, 19, 7, 1, 1, black)
    // Iconic diagonal white reflection inside each lens
    rect(g, 7, 6, 1, 1, white)
    rect(g, 8, 7, 1, 1, white)
    rect(g, 15, 6, 1, 1, white)
    rect(g, 16, 7, 1, 1, white)
  }
  if (pose === 'waving') {
    // Wide friendly grin
    rect(g, 10, 10, 5, 1, P.skinShadow, 0.85)
    rect(g, 11, 11, 3, 1, P.skinShadow, 0.7)
  } else {
    rect(g, 12, 10, 3, 1, P.skinShadow, 0.8) // mouth
  }

  if (pose === 'scan') {
    rect(g, 4, 16, 4, 8, P.coatDark)
    rect(g, 7, 12, 4, 7, P.coat)
    rect(g, 19, 16, 4, 7, P.coatDark)
    rect(g, 17, 12, 4, 7, P.coat)
    rect(g, 9, 10, 4, 3, P.skin)
    rect(g, 15, 10, 4, 3, P.skin)
    rect(g, 12, 6, 7, 9, P.phone)
    rect(g, 13, 7, 5, 7, P.phoneScreen)
    const pulse = 0.55 + 0.45 * Math.sin(timeMs / 120)
    rect(g, 13, 7, 5, 3, P.phoneScreenBright, pulse)
    const sweep = (timeMs / 520) % 1
    poly(g, [19, 8, 31, 3 + sweep * 7, 31, 11 + sweep * 7], P.scanBeam, 0.2)
    rect(g, 19, Math.round(7 + sweep * 8), 12, 1, P.scanBeam, 0.5)
  } else if (pose === 'waving') {
    // Left arm hangs normally
    rect(g, 3, 16, 4, 18, P.coatDark)
    rect(g, 3, 33, 4, 3, P.skin)
    // Right arm raised over the head, hand sways side-to-side
    rect(g, 19, 8, 4, 9, P.coatDark) // upper arm
    const tilt = Math.round(Math.sin(timeMs / 140) * 2)
    rect(g, 18 + tilt, 1, 4, 8, P.coatDark) // forearm
    rect(g, 17 + tilt, -3, 5, 4, P.skin) // hand
    rect(g, 17 + tilt, -4, 5, 1, P.skinShadow, 0.6) // fingertip shading
  } else {
    const sw = pose === 'walk' ? (frame === 0 ? 2 : -2) : 0
    rect(g, 3, 16, 4, 18 - Math.abs(sw), P.coatDark)
    rect(g, 19, 16, 4, 18 - Math.abs(sw), P.coatDark)
    rect(g, 3, 33 - sw, 4, 3, P.skin)
    rect(g, 19, 33 + sw, 4, 3, P.skin)
  }

  // ─── Accessory: top hat (level 2+) — perches above the hair ─────────────
  if (level >= 2) {
    rect(g, 7, 1, 12, 1, 0x141418) // wide brim
    rect(g, 9, -4, 8, 5, 0x141418) // hat body
    rect(g, 9, 0, 8, 1, 0xc4443a) // red band
    rect(g, 9, -4, 8, 1, 0x2c2c34) // top highlight
  }

  // ─── Accessory: briefcase (level 4+) — held in the left hand ────────────
  // Skip while scanning (both hands hold the phone) and while waving (the
  // right arm is raised — left hand still hangs, but the briefcase clashes
  // with the broad grin pose). Walks/idle keep it.
  if (level >= 4 && pose !== 'scan' && pose !== 'waving') {
    const sw = pose === 'walk' ? (frame === 0 ? 2 : -2) : 0
    const handY = 36 - sw
    rect(g, -1, handY, 7, 7, 0x6e4a2e) // briefcase body
    rect(g, -1, handY, 7, 1, 0x523620) // top edge
    rect(g, -1, handY + 6, 7, 1, 0x3d2814) // bottom shadow
    rect(g, 2, handY - 2, 3, 2, 0x523620) // handle
    rect(g, 2, handY + 3, 3, 1, 0xd9b45a) // brass latch
  }

  // ─── Accessory: gold sparkles (level 5) — twinkling around the figure ────
  if (level >= 5) {
    const sparkles: Array<[number, number]> = [
      [1, 6],
      [24, 9],
      [-2, 22],
      [26, 19],
      [4, 36],
      [22, 34],
    ]
    for (let i = 0; i < sparkles.length; i++) {
      const [sx, sy] = sparkles[i]
      const tw = 0.35 + 0.55 * Math.sin(timeMs / 220 + i * 1.4)
      if (tw > 0.45) {
        rect(g, sx, sy, 1, 1, 0xf4d77a, Math.min(1, tw))
        // a small cross-twinkle at the peak
        if (tw > 0.78) {
          rect(g, sx - 1, sy, 3, 1, 0xf4d77a, 0.4)
          rect(g, sx, sy - 1, 1, 3, 0xf4d77a, 0.4)
        }
      }
    }
  }
}

export function createCharacter(): Character {
  const container = new Container()
  container.label = 'character'
  container.pivot.set(13, 0)
  const body = new Graphics()
  body.filters = [makeOutline()] // black silhouette outline, like the objects
  container.addChild(body)

  let pose: Pose = 'idle'
  let arrive: Arrive = 'idle'
  let x = CHARACTER_HOME_X
  let targetX = x
  let facing = 1
  let speed = 120
  let walkAcc = 0
  let frame = 0
  let stageElapsed = 0
  let level = 0 // 0..5, set externally each time coverage changes

  container.position.set(Math.round(x), BASE_TOP)
  drawBody(body, pose, frame, 0, 0, level)

  // Switch to the on-arrival pose once the walk reaches the target.
  function enterArrive() {
    if (arrive === 'fall') {
      pose = 'falling'
      stageElapsed = 0
    } else if (arrive === 'fly') {
      pose = 'flying'
      stageElapsed = 0
      facing = 1 // keep plane coords un-mirrored
    } else if (arrive === 'scan') {
      pose = 'scan'
    } else if (arrive === 'wave') {
      pose = 'waving'
      facing = 1 // face the viewer
    } else {
      pose = 'idle'
      facing = 1
    }
  }

  return {
    container,
    command(mode, tx) {
      arrive =
        mode === 'scan'
          ? 'scan'
          : mode === 'die'
            ? 'fall'
            : mode === 'fly'
              ? 'fly'
              : mode === 'wave'
                ? 'wave'
                : 'idle'
      // 'wave' is rooted at the figure's current spot (no walk); 'idle' goes home.
      targetX = mode === 'idle' ? CHARACTER_HOME_X : mode === 'wave' ? x : tx
      if (Math.abs(targetX - x) > 1.5) {
        pose = 'walk'
        speed = Math.max(70, Math.abs(targetX - x) / 1.1)
      } else {
        enterArrive()
      }
    },
    update(dtMs, timeMs) {
      if (pose === 'walk') {
        const dir = targetX > x ? 1 : -1
        facing = dir
        x += (dir * speed * dtMs) / 1000
        if ((dir > 0 && x >= targetX) || (dir < 0 && x <= targetX)) {
          x = targetX
          enterArrive()
        }
        walkAcc += dtMs
        frame = Math.floor(walkAcc / 150) % 2
      } else if (pose === 'falling') {
        stageElapsed += dtMs
        if (stageElapsed >= FALL_MS) pose = 'fallen'
      } else if (pose === 'flying') {
        stageElapsed += dtMs
        if (stageElapsed >= BOARD_MS + FLY_MS) pose = 'scan' // reappear + scan
      } else {
        frame = 0
      }
      // Walk has a 1px step bob; when idle/waving the figure gently breathes.
      const breathe =
        pose === 'idle' || pose === 'waving'
          ? (Math.sin(timeMs / 700) > 0.6 ? 1 : 0)
          : 0
      const bob = pose === 'walk' && frame === 1 ? 1 : breathe
      container.position.set(Math.round(x), BASE_TOP - bob)
      container.scale.x = facing
      drawBody(body, pose, frame, timeMs, stageElapsed, level)
    },
    setLevel(n) {
      level = Math.max(0, Math.min(5, Math.floor(n)))
    },
  }
}
